import { useState } from 'react';
import { ArrowLeft, LogOut, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@actions/services/firestore/browser/firebase';
import { useVoxityLogout } from '@voxityai/auth/hooks';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useInitialData from '../../contexts/InitialDataContext/useInitialData';
import { useIsAdmin } from '@/hooks';
import readLastAgentId from '@/helpers/lastAgentId/readLastAgentId';
import { AvatarContainer, UserInfo, UserName, UserLabel, LogoutItem, MenuItem } from './styled';
import type { AvatarMenuProps } from './types';

export function AvatarMenu({ variant }: AvatarMenuProps) {
    const { isLoadingInitialData } = useInitialData();
    const { isAdmin } = useIsAdmin();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const authServerUrl = import.meta.env.VITE_AUTH_SERVER_URL || '';
    const redirectUri = window.location.origin;
    const triggerVoxityLogout = useVoxityLogout({
        auth,
        authServerUrl,
        redirectUri,
    });

    const user = auth.currentUser;

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await triggerVoxityLogout();
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            setIsLoggingOut(false);
            setOpen(false);
        }
    };

    const handleNavigateToAdminPanel = () => {
        setOpen(false);
        navigate('/admin/dashboard');
    };

    const handleExitAdminPanel = () => {
        setOpen(false);
        const lastAgentId = readLastAgentId();
        if (lastAgentId) {
            navigate(`/agents/${lastAgentId}/workflows`);
            return;
        }
        navigate('/');
    };

    const renderAvatar = (displayName: string) => {
        if (user?.photoURL) {
            return (
                <img src={user.photoURL} alt={displayName} className="w-full h-full object-cover" />
            );
        }
        return (
            <span className="text-white font-medium">{displayName.charAt(0).toUpperCase()}</span>
        );
    };

    if (isLoadingInitialData || !user) {
        return (
            <div className="h-auto p-3">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex flex-col gap-2 flex-1">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                    </div>
                </div>
            </div>
        );
    }

    const displayName = user.displayName || user.email?.split('@')[0] || 'User';
    const userEmail = user.email || '';

    return (
        <DropdownMenu open={open} modal={false} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className="w-full justify-start h-auto p-3 hover:bg-sidebar-accent"
                >
                    <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                        <AvatarContainer>{renderAvatar(displayName)}</AvatarContainer>
                        <UserInfo>
                            <UserName>{displayName}</UserName>
                            <UserLabel>{userEmail}</UserLabel>
                        </UserInfo>
                    </div>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[280px] p-2 z-[1002]" side="right" align="end">
                <div className="flex items-center gap-3 p-2">
                    <AvatarContainer>{renderAvatar(displayName)}</AvatarContainer>
                    <UserInfo>
                        <UserName>{displayName}</UserName>
                        <UserLabel>{userEmail}</UserLabel>
                    </UserInfo>
                </div>
                <DropdownMenuSeparator />
                {variant === 'app' && isAdmin ? (
                    <MenuItem onClick={handleNavigateToAdminPanel}>
                        <ShieldCheck className="h-4 w-4" />
                        <span>Admin panel</span>
                    </MenuItem>
                ) : null}
                {variant === 'admin' ? (
                    <MenuItem onClick={handleExitAdminPanel}>
                        <ArrowLeft className="h-4 w-4" />
                        <span>Exit admin panel</span>
                    </MenuItem>
                ) : null}
                {(variant === 'admin' || (variant === 'app' && isAdmin)) && (
                    <DropdownMenuSeparator />
                )}
                <LogoutItem onClick={handleLogout} disabled={isLoggingOut}>
                    <LogOut className="h-4 w-4" />
                    <span>{isLoggingOut ? 'Signing out...' : 'Sign out'}</span>
                </LogoutItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
